import Vue from "vue";
import Router from "vue-router";

import { TokenType } from "@/helpers/Constants";
import { tokenOptions } from '@/helpers/claims';
import { checkPermission } from "@/mixins/canWrite";

Vue.use(Router);

// Polyfill minimal global fetch/Headers/Response using XHR for old environments (old TVs)
// Do not override if native implementations exist. Keep polyfill minimal and non-invasive.
(function(global) {
  if (!global) return;
  try {
    const hasFetch = typeof global.fetch === 'function';
    const hasHeaders = typeof global.Headers !== 'undefined';
    const hasResponse = typeof global.Response !== 'undefined';
    const hasRequest = typeof global.Request !== 'undefined';

    // Minimal Headers polyfill
    if (!hasHeaders) {
      var HeadersPoly = function(init) {
        this.map = Object.create(null);
        if (init) {
          try {
            if (typeof init.forEach === 'function') {
              init.forEach((v, k) => this.append(k, v));
            } else if (Array.isArray(init)) {
              init.forEach(([k, v]) => this.append(k, v));
            } else {
              for (const k in init) { if (Object.prototype.hasOwnProperty.call(init, k)) this.append(k, init[k]); }
            }
          } catch (e) { /* best-effort */ }
        }
      };
      HeadersPoly.prototype.append = function(k, v) {
        k = String(k).toLowerCase();
        const existing = this.map[k];
        this.map[k] = existing ? existing + ', ' + v : String(v);
      };
      HeadersPoly.prototype.set = function(k, v) { this.map[String(k).toLowerCase()] = String(v) };
      HeadersPoly.prototype.get = function(k) { return this.map[String(k).toLowerCase()] || null };
      HeadersPoly.prototype.has = function(k) { return String(k).toLowerCase() in this.map };
      HeadersPoly.prototype.delete = function(k) { delete this.map[String(k).toLowerCase()] };
      HeadersPoly.prototype.forEach = function(cb, thisArg) { for (const k in this.map) { if (Object.prototype.hasOwnProperty.call(this.map, k)) cb.call(thisArg, this.map[k], k, this); } };
      // iterable support: for...of
      HeadersPoly.prototype.keys = function() { const arr = Object.keys(this.map); let i = 0; return { next: () => ({ value: arr[i++], done: i > arr.length }) } };
      HeadersPoly.prototype.values = function() { const arr = Object.keys(this.map).map(k => this.map[k]); let i = 0; return { next: () => ({ value: arr[i++], done: i > arr.length }) } };
      HeadersPoly.prototype.entries = function() { const arr = Object.keys(this.map).map(k => [k, this.map[k]]); let i = 0; return { next: () => ({ value: arr[i++], done: i > arr.length }) } };
      try { HeadersPoly.prototype[Symbol.iterator] = HeadersPoly.prototype.entries } catch (e) { /* Symbol may not exist */ }
      global.Headers = HeadersPoly;
    }

    // Minimal Response polyfill
    if (!hasResponse) {
      var ResponsePoly = function(bodyText, options) {
        options = options || {};
        this.status = options.status || 200;
        this.statusText = options.statusText || '';
        this.ok = this.status >= 200 && this.status < 300;
        this.headers = new global.Headers(options.headers || {});
        this._bodyText = typeof bodyText === 'string' ? bodyText : (bodyText == null ? '' : String(bodyText));
      };
      ResponsePoly.prototype.text = function() { return Promise.resolve(this._bodyText) };
      ResponsePoly.prototype.json = function() { try { return Promise.resolve(JSON.parse(this._bodyText)) } catch (e) { return Promise.reject(e) } };
      ResponsePoly.prototype.clone = function() { return new ResponsePoly(this._bodyText, { status: this.status, statusText: this.statusText, headers: this.headers }) };
      global.Response = ResponsePoly;
    }

    // Minimal Request polyfill (used rarely but helps compatibility)
    if (!hasRequest) {
      var RequestPoly = function(input, init) {
        if (typeof input === 'string') this.url = input;
        else if (input && input.url) this.url = input.url;
        else this.url = '';
        init = init || {};
        this.method = (init.method || (input && input.method) || 'GET').toUpperCase();
        this.headers = new global.Headers((init && init.headers) || (input && input.headers) || {});
        this.body = init && init.body || null;
      };
      global.Request = RequestPoly;
    }

    // Minimal fetch polyfill using XMLHttpRequest
    if (!hasFetch && typeof global.XMLHttpRequest !== 'undefined') {
      global.fetch = function(fetchInput, opts) {
        // Accept Request object or (url, opts)
        if (fetchInput && typeof fetchInput === 'object' && fetchInput.url) {
          opts = opts || {};
          // prefer explicit opts, otherwise copy from Request-like
          opts.method = opts.method || fetchInput.method;
          opts.headers = opts.headers || fetchInput.headers;
          opts.body = ('body' in opts) ? opts.body : fetchInput.body;
          fetchInput = fetchInput.url;
        }
        opts = opts || {};
        return new Promise(function(resolve, reject) {
          try {
            var xhr = new global.XMLHttpRequest();
            xhr.open((opts.method || 'GET'), fetchInput, true);

            // set headers
            try {
              var hdrs = opts.headers || {};
              if (typeof hdrs.forEach === 'function') {
                hdrs.forEach(function(v, k) { try { xhr.setRequestHeader(k, v) } catch (e) {} });
              } else {
                for (var k in hdrs) { if (Object.prototype.hasOwnProperty.call(hdrs, k)) { try { xhr.setRequestHeader(k, hdrs[k]) } catch (e) {} } }
              }
            } catch (e) { }

            xhr.onload = function() {
              var allHeaders = '';
              try { allHeaders = xhr.getAllResponseHeaders() || '' } catch (e) { allHeaders = '' }
              var headers = new global.Headers();
              if (allHeaders) {
                allHeaders.trim().split(/\r?\n/).forEach(function(line) {
                  var parts = line.split(':');
                  var key = parts.shift().trim();
                  var val = parts.join(':').trim();
                  if (key) headers.append(key, val);
                });
              }
              var responseOptions = { status: xhr.status, statusText: xhr.statusText, headers: headers };
              resolve(new global.Response(xhr.responseText, responseOptions));
            };

            xhr.onerror = function() { reject(new TypeError('Network request failed')) };
            xhr.ontimeout = function() { reject(new TypeError('Network request failed')) };

            try {
              if ('body' in opts && opts.body != null) xhr.send(opts.body);
              else xhr.send(null);
            } catch (e) {
              try { xhr.send(typeof opts.body === 'string' ? opts.body : JSON.stringify(opts.body)) } catch (e2) { try { xhr.send(null) } catch (e3) {} }
            }
          } catch (err) { reject(err) }
        });
      };
    }
  } catch (e) { /* swallow polyfill errors */ }
})(typeof window !== 'undefined' ? window : (typeof self !== 'undefined' ? self : (typeof global !== 'undefined' ? global : this)));

// Header values are limited to ISO-8859-1; page titles may hold a typographic dash, NBSP etc. and Headers.set would throw
const toHeaderValue = v => (v ?? '').toString().replace(/[\uD800-\uDBFF][\uDC00-\uDFFF]|[^\x20-\x7E]/g, c => {
  try { return encodeURIComponent(c) } catch (e) { return '' } // lone surrogate
});

export default {
  methods: {
    requestApi(url = "", method = "get", data = {}, options = {}) {
      if (typeof (method) !== "string") {
        options = method || {};
        method = options.method || "get";
        data = options.data || {};
      }
      const {
        showSpinner = true,
        showError = true,
        contentType = "application/json",
        maxRetries = 2,
        skipAuthorization = false,
        skipHeaders = false,
        abortController = null,
        includeCookie = false
      } = options;

      if (showSpinner) {
        if (typeof (showSpinner) === "function") {
          showSpinner(true);
        } else {
          this.$store.commit("addRequest");
          this.$store.commit("setLoad", true);
        }
      }

      let authorization = this.$store.state.token.bearer;
      if (!authorization) authorization = sessionStorage.token || localStorage.token;
      else if ((this.$store.state.token.type & (TokenType.Autogenerated | TokenType.AdminUI | TokenType.Link)) === TokenType.AdminUI) {
        localStorage.token = sessionStorage.token = authorization;
      }

      const headers = new Headers({
        "Access-Control-Allow-Origin": location.origin
      });
      if (contentType && !(data instanceof FormData) && contentType.toLowerCase() !== 'multipart/form-data') {
        headers.set("Content-Type", contentType);
      }
      if (authorization && !skipAuthorization) {
        headers.set("Authorization", authorization);
      }
      if (!skipHeaders) {
        headers.set("RefUrl", toHeaderValue(location.href));
        headers.set("RefPage", (this.$store.state.token.type & TokenType.Site) ? 'dynamic' : toHeaderValue(this.$route.name));
      }
      if (includeCookie && document.cookie) {
        headers.set("Cookies", toHeaderValue(document.cookie));
      }

      const buildOptions = () => {
        const opts = {
          method: method.toUpperCase(),
          headers
        };
        if (abortController) opts.signal = abortController.signal;
        if (method.toLowerCase() !== "get") {
          if (data instanceof FormData) {
            opts.body = data;
          } else if ((contentType || '').toLowerCase() === "application/json") {
            opts.body = JSON.stringify(data);
          } else {
            opts.body = data;
          }
        }
        return opts;
      };

      const reportError = (info) => {
        if (!showError) return;
        if (typeof showError === "function") showError(info);
        else this.$awn.warning(info.message);
      };

      const defError = () => {
        let path = url;
        const api = url.split('/api');
        if (api.length > 1) path = api.pop();
        else {
          const lambda = url.split('amazonaws.com/');
          if (lambda.length > 1) path = lambda.pop();
        }
        return `Can not ${method} on ${path}`;
      };

      const attemptFetch = (attempt) => {
        return fetch(url, buildOptions()).then(async(resp) => {
          if (!resp.ok) {
            if (resp.status === 401) this.$router.push('/admin/login');
            let text = await resp.text().catch(() => "");
            if (!text && showError) text = defError();
            try { if (text) text = JSON.parse(text); } catch { }
            if (location.origin.includes('localhost') || location.origin.includes('stage') || checkPermission(tokenOptions.SuperAdmin, this.$store.state.token.permissions)) {
              console.warn(text.message ? text.message + '\n' + text.stackTrace : text);
            }
            const serverMsg = (text && typeof text === 'object') ? (text.message || text.error) : (typeof text === 'string' ? text : null);
            const isBusiness = !!(text && typeof text === 'object' && (text.severity & 256));
            reportError({ kind: isBusiness ? 'business' : 'server', status: resp.status, message: serverMsg || defError() });
            return { ok: false, body: text };
          }

          const res = { ok: true, bodyText: await resp.text() };
          try { if (res.bodyText) res.body = JSON.parse(res.bodyText); } catch { res.body = res.bodyText }
          // Business-level failure on a 2xx response (server return { success: false, ... }, but this is a 2xx HTTP status)
          if (res.body && typeof res.body === 'object' && res.body.success === false) {
            const msg = res.body.error || res.body.message;
            reportError({ kind: 'business', status: 200, message: (typeof msg === 'string' && msg) ? msg : defError() });
          }
          return res;
        }).catch((err) => {
          // retry only on network/CORS errors
          if (attempt < maxRetries) return attemptFetch(attempt + 1);
          reportError({ kind: 'network', status: 0, message: defError() });
          return { ok: false, body: err.message || defError() };
        });
      };

      const finalize = () => {
        if (showSpinner) {
          if (typeof (showSpinner) === "function") {
            showSpinner(false);
          } else {
            this.$store.commit("removeRequest");
            if (this.$store.state.activeRequests === 0) this.$store.commit("setLoad", false);
          }
        }
      };

      // Avoid using Promise.prototype.finally for compatibility with older devices
      return attemptFetch(0).then(
        (res) => { finalize(); return res },
        (err) => { finalize(); throw err }
      );
    }
  }
};
